import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../../data';
import { GameState } from '../../systems/GameState';

interface VirusPuzzleModalProps {
  isOpen: boolean;
  onClose: () => void;
  grid: number[][];
  onCellClick: (row: number, col: number) => void;
  onSubmit: () => void;
  timeLeft: number;
  patterns: Record<string, { name: string; description: string }>;
  solvedPatterns: Set<string>;
  hintCells?: {row: number, col: number}[];
}

export const VirusPuzzleModal: React.FC<VirusPuzzleModalProps> = ({
  isOpen,
  onClose,
  grid,
  onCellClick,
  onSubmit,
  timeLeft,
  patterns,
  solvedPatterns,
  hintCells = [],
}) => {
  const gameState = GameState.getInstance();
  const showHint = gameState.trustLevel > 50;

  const isHintCell = (row: number, col: number) => {
    return hintCells.some(cell => cell.row === row && cell.col === col);
  };

  useEffect(() => {
    if (isOpen && timeLeft <= 0) {
      onClose();
    }
  }, [isOpen, timeLeft, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="virus-puzzle-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="virus-puzzle-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <div className="virus-puzzle-header">
              <h2 className="virus-puzzle-title">{CONSTANTS.VIRUS_PUZZLE.modalTitle}</h2>
              <div className="virus-puzzle-timer">TIME LEFT: {timeLeft}s</div>
            </div>
            <p className="virus-puzzle-instructions">{CONSTANTS.VIRUS_PUZZLE.instructions}</p>
            
            <div className="virus-puzzle-content">
              <div className="virus-puzzle-grid">
                {grid.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <button
                      key={`${rowIndex}-${colIndex}`}
                      className={`virus-puzzle-cell cell-${cell} ${isHintCell(rowIndex, colIndex) ? 'hinted' : ''}`}
                      onClick={() => onCellClick(rowIndex, colIndex)}
                    >
                      {cell}
                    </button>
                  ))
                )}
              </div>
              <div className="virus-puzzle-patterns">
                <h3>Required Patterns:</h3>
                <ul>
                  {Object.entries(patterns).map(([key, pattern]) => (
                    <li key={key} className={solvedPatterns.has(key) ? 'solved' : ''}>
                      <strong>{pattern.name}:</strong> {pattern.description}
                    </li>
                  ))}
                </ul>
                {showHint && <p className="virus-puzzle-hint">AI HINT: {CONSTANTS.VIRUS_PUZZLE.aiHint}</p>}
              </div>
            </div>

            <div className="virus-puzzle-footer">
              <button className="virus-puzzle-submit-btn" onClick={onSubmit}>
                {CONSTANTS.VIRUS_PUZZLE.checkButtonText}
              </button>
              <button className="virus-puzzle-close-btn" onClick={onClose}>
                Abort Protocol
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
